import moment from 'moment';

/**
 * Node progress statuses, normalized for display.
 *
 * The API sends the raw DB values (`pending`, `in_progress`, `completed`, `skipped`,
 * `delayed`). Comparing against them directly after a bare `.toUpperCase()` silently fails
 * for the underscored ones, so every consumer must normalize through
 * `normalizeNodeStatus` first.
 */
export const NODE_STATUS = {
	PENDING: 'PENDING',
	IN_PROGRESS: 'IN PROGRESS',
	COMPLETED: 'COMPLETED',
	SKIPPED: 'SKIPPED',
	DELAYED: 'DELAYED',
};

/**
 * `in_progress` -> `IN PROGRESS`. Returns '' for null/undefined.
 *
 * `isTripClosed` is a defensive safety net (spec §7): the backend force-completes every
 * node when a trip closes, but nothing on the frontend enforces that a closed trip can
 * never display a non-Completed node — a stale read, a race with that backend write, or a
 * legacy row from before the force-complete existed would otherwise still render
 * "Pending"/"In Progress" on a trip that is over. Callers that know the trip's own status
 * pass it through so any such status is displayed as Completed regardless of what the API
 * actually sent for that node.
 */
export const normalizeNodeStatus = (status, isTripClosed) => {
	const normalized = status?.replace(/_/g, ' ')?.toUpperCase?.() || '';
	return isTripClosed ? NODE_STATUS.COMPLETED : normalized;
};

export const isNodeCompleted = (status, isTripClosed) =>
	normalizeNodeStatus(status, isTripClosed) === NODE_STATUS.COMPLETED;

export const isNodeInProgress = (status, isTripClosed) =>
	normalizeNodeStatus(status, isTripClosed) === NODE_STATUS.IN_PROGRESS;

/**
 * Format a tracking timestamp for display in IST.
 *
 * Tracking timestamps (`trip_map_path.time`, `all_tracking_info.current_location.timestamp`,
 * node `last_updated`) come back as naive ISO strings that are already UTC — no offset
 * marker. They must be read as UTC and shifted to IST, not passed through local time.
 */
export const formatTrackingTimeIST = (value, format = 'DD MMM, hh:mm A') => {
	if (!value) return '-';

	const parsed = moment.utc(value);
	return parsed.isValid() ? parsed.utcOffset('+05:30').format(format) : '-';
};
